import type { AnalyticsEvent } from "./events";

export type Provider = {
  key: string;
  /** Ставит стаб-очередь SDK и подтягивает удалённый скрипт. */
  inject: () => void;
  /** Отсутствует у площадок без событийного API (Mailchimp). */
  send?: (event: AnalyticsEvent) => void;
};

const META_ID = "488527050969952";
const TIKTOK_ID = "D8LPPO3C77U4U91BANBG";
const GA4_ID = "G-KFYMCW8HB6";
const PINTEREST_ID = "2612865304869";
const SNAP_ID = "cf4c0ebc-9b46-4049-9454-434d2221ecb0";
const MAILCHIMP_SRC =
  "https://chimpstatic.com/mcjs-connected/js/users/60617a57dda36aad704afd113/b32af5e6f59d372fc7c846a6f.js";

function appendScript(src: string): void {
  const s = document.createElement("script");
  s.async = true;
  s.src = src;
  document.head.appendChild(s);
}

/* ---------- Meta ---------- */

function injectMeta(): void {
  const w = window as any;
  if (w.fbq) return;
  const n: any = (w.fbq = function (...args: any[]) {
    n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
  });
  if (!w._fbq) w._fbq = n;
  n.push = n;
  n.loaded = true;
  n.version = "2.0";
  n.queue = [];
  appendScript("https://connect.facebook.net/en_US/fbevents.js");
  w.fbq("init", META_ID);
}

function sendMeta(e: AnalyticsEvent): void {
  const fbq = (window as any).fbq;
  if (!fbq) return;
  switch (e.name) {
    case "page_view":
      return fbq("track", "PageView");
    case "view_content":
      return fbq("track", "ViewContent", {
        content_ids: [e.contentId],
        content_name: e.contentName,
        value: e.value,
        currency: e.currency,
      });
    case "initiate_checkout":
      return fbq("track", "InitiateCheckout", {
        content_ids: [e.contentId],
        content_name: e.contentName,
        value: e.value,
        currency: e.currency,
      });
    case "purchase":
      return fbq("track", "Purchase", {
        content_ids: [e.contentId],
        content_name: e.contentName,
        value: e.value,
        currency: e.currency,
      });
    case "lead":
      return fbq("track", "Lead", { content_name: e.source });
  }
}

/* ---------- TikTok ---------- */

function injectTikTok(): void {
  const w = window as any;
  if (w.TiktokAnalyticsObject) return;
  w.TiktokAnalyticsObject = "ttq";
  const ttq: any = (w.ttq = w.ttq || []);
  ttq.methods = [
    "page", "track", "identify", "instances", "debug", "on", "off", "once",
    "ready", "alias", "group", "enableCookie", "disableCookie", "holdConsent",
    "revokeConsent", "grantConsent",
  ];
  ttq.setAndDefer = function (target: any, method: string) {
    target[method] = function (...args: any[]) {
      target.push([method].concat(args));
    };
  };
  ttq.methods.forEach((m: string) => ttq.setAndDefer(ttq, m));
  ttq.instance = function (id: string) {
    const inst = (ttq._i || {})[id] || [];
    ttq.methods.forEach((m: string) => ttq.setAndDefer(inst, m));
    return inst;
  };
  ttq.load = function (id: string, options?: any) {
    const url = "https://analytics.tiktok.com/i18n/pixel/events.js";
    ttq._i = ttq._i || {};
    ttq._i[id] = [];
    ttq._i[id]._u = url;
    ttq._t = ttq._t || {};
    ttq._t[id] = Date.now();
    ttq._o = ttq._o || {};
    ttq._o[id] = options || {};
    appendScript(`${url}?sdkid=${id}&lib=ttq`);
  };
  ttq.loaded = true;
  ttq.load(TIKTOK_ID);
}

function sendTikTok(e: AnalyticsEvent): void {
  const ttq = (window as any).ttq;
  if (!ttq) return;
  switch (e.name) {
    case "page_view":
      return ttq.page();
    case "view_content":
      return ttq.track("ViewContent", {
        content_id: e.contentId,
        content_name: e.contentName,
        value: e.value,
        currency: e.currency,
      });
    case "initiate_checkout":
      return ttq.track("InitiateCheckout", {
        content_id: e.contentId,
        content_name: e.contentName,
        value: e.value,
        currency: e.currency,
      });
    case "purchase":
      return ttq.track("CompletePayment", {
        content_id: e.contentId,
        content_name: e.contentName,
        value: e.value,
        currency: e.currency,
      });
    case "lead":
      return ttq.track("SubmitForm", { content_name: e.source });
  }
}

/* ---------- Google Analytics 4 ---------- */

function injectGa4(): void {
  const w = window as any;
  if (w.gtag) return;
  w.dataLayer = w.dataLayer || [];
  // Namespace-функция gtag обязана читать `arguments`, поэтому не стрелочная.
  w.gtag = function () {
    // eslint-disable-next-line prefer-rest-params
    w.dataLayer.push(arguments);
  };
  appendScript(`https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`);
  w.gtag("js", new Date());
  // send_page_view отключён: просмотры шлёт useRouteAnalytics, иначе первый
  // задвоится со стартовым событием тега.
  w.gtag("config", GA4_ID, { send_page_view: false });
}

function sendGa4(e: AnalyticsEvent): void {
  const gtag = (window as any).gtag;
  if (!gtag) return;
  switch (e.name) {
    case "page_view":
      return gtag("event", "page_view", { page_location: window.location.href });
    case "view_content":
      return gtag("event", "view_item", {
        items: [{ item_id: e.contentId, item_name: e.contentName }],
        value: e.value,
        currency: e.currency,
      });
    case "initiate_checkout":
      return gtag("event", "begin_checkout", {
        items: [{ item_id: e.contentId, item_name: e.contentName }],
        value: e.value,
        currency: e.currency,
      });
    case "purchase":
      return gtag("event", "purchase", {
        transaction_id: e.orderId,
        items: [{ item_id: e.contentId, item_name: e.contentName }],
        value: e.value,
        currency: e.currency,
      });
    case "lead":
      return gtag("event", "generate_lead", { method: e.source });
  }
}

/* ---------- Pinterest ---------- */

function injectPinterest(): void {
  const w = window as any;
  if (!w.pintrk) {
    const n: any = (w.pintrk = function (...args: any[]) {
      n.queue.push(args);
    });
    n.queue = [];
    n.version = "3.0";
    appendScript("https://s.pinimg.com/ct/core.js");
  }
  w.pintrk("load", PINTEREST_ID, { em: "" });
}

function sendPinterest(e: AnalyticsEvent): void {
  const pintrk = (window as any).pintrk;
  if (!pintrk) return;
  switch (e.name) {
    case "page_view":
      return pintrk("track", "pagevisit");
    case "view_content":
      return pintrk("track", "viewcategory", { product_id: e.contentId });
    // У Pinterest нет стандартного события «начал оплату» — addtocart ближе
    // всего по смыслу в их воронке.
    case "initiate_checkout":
      return pintrk("track", "addtocart", {
        product_id: e.contentId,
        value: e.value,
        currency: e.currency,
      });
    case "purchase":
      return pintrk("track", "checkout", {
        order_id: e.orderId,
        value: e.value,
        currency: e.currency,
      });
    case "lead":
      return pintrk("track", "lead", { lead_type: e.source });
  }
}

/* ---------- Snap ---------- */

function injectSnap(): void {
  const w = window as any;
  if (w.snaptr) return;
  const a: any = (w.snaptr = function (...args: any[]) {
    a.handleRequest ? a.handleRequest.apply(a, args) : a.queue.push(args);
  });
  a.queue = [];
  appendScript("https://sc-static.net/scevent.min.js");
  w.snaptr("init", SNAP_ID, {});
}

function sendSnap(e: AnalyticsEvent): void {
  const snaptr = (window as any).snaptr;
  if (!snaptr) return;
  switch (e.name) {
    case "page_view":
      return snaptr("track", "PAGE_VIEW");
    case "view_content":
      return snaptr("track", "VIEW_CONTENT", { item_ids: [e.contentId] });
    case "initiate_checkout":
      return snaptr("track", "START_CHECKOUT", {
        item_ids: [e.contentId],
        price: e.value,
        currency: e.currency,
      });
    case "purchase":
      return snaptr("track", "PURCHASE", {
        transaction_id: e.orderId,
        item_ids: [e.contentId],
        price: e.value,
        currency: e.currency,
      });
    case "lead":
      return snaptr("track", "SIGN_UP", { sign_up_method: e.source });
  }
}

/* ---------- Mailchimp ---------- */

/** Connected-site скрипт: событий не принимает, просто должен быть на странице. */
function injectMailchimp(): void {
  appendScript(MAILCHIMP_SRC);
}

export const PROVIDERS: readonly Provider[] = [
  { key: "meta", inject: injectMeta, send: sendMeta },
  { key: "tiktok", inject: injectTikTok, send: sendTikTok },
  { key: "ga4", inject: injectGa4, send: sendGa4 },
  { key: "pinterest", inject: injectPinterest, send: sendPinterest },
  { key: "snap", inject: injectSnap, send: sendSnap },
  { key: "mailchimp", inject: injectMailchimp },
];
